import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { fetchAuditLogs } from "../requests/request";
import type {
  AuditLog,
  AuditLogsPagination,
  AuditLogsQueryParams,
} from "../types";

interface UseAuditLogsListResult {
  data: AuditLog[];
  pagination: AuditLogsPagination | null;
  isFetching: boolean;
  hasFetched: boolean;
  error: string | null;
  /** Status code of the last failed response (if any). Useful for 403 handling. */
  errorStatus: number | null;
  refetch: () => void;
}

/**
 * Stable JSON serialization of the query params so the effect dep array
 * doesn't fire on object identity changes when contents are equivalent.
 *
 * Keys are sorted to make the result deterministic. Undefined / null /
 * empty values are stripped so they don't generate noisy refetches.
 */
function stableParamsKey(params: AuditLogsQueryParams): string {
  const cleaned: Record<string, unknown> = {};
  Object.keys(params)
    .sort()
    .forEach((key) => {
      const v = (params as Record<string, unknown>)[key];
      if (v === undefined || v === null || v === "") return;
      if (Array.isArray(v) && v.length === 0) return;
      cleaned[key] = v;
    });
  return JSON.stringify(cleaned);
}

/**
 * Fetches the paginated list of audit logs.
 *
 * UX contract (mirrors the brief):
 *   - First mount: `isFetching=true`, `hasFetched=false`, `data=[]`.
 *   - Refilter / repaginate: `isFetching=true`, `hasFetched=true`,
 *     `data` keeps the previous rows so the page can render an overlay
 *     instead of unmounting the table.
 *   - On error: `data=[]`, `error=<backend message>`, `errorStatus=<code>`.
 */
export function useAuditLogsList(
  params: AuditLogsQueryParams
): UseAuditLogsListResult {
  const api = useAxiosAuth0();
  const [data, setData] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState<AuditLogsPagination | null>(
    null
  );
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [refreshNonce, setRefreshNonce] = useState<number>(0);

  const paramsKey = stableParamsKey(params);
  const requestIdRef = useRef<number>(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setIsFetching(true);
    setError(null);
    setErrorStatus(null);

    fetchAuditLogs(api, params)
      .then((response) => {
        if (requestIdRef.current !== requestId) return;
        const { status, data: body } = response;
        if (status >= 400 || !body || body.success === false) {
          setData([]);
          setPagination(null);
          setError(body?.message ?? `Request failed with status ${status}`);
          setErrorStatus(status);
          return;
        }
        setData(Array.isArray(body.data) ? body.data : []);
        setPagination(body.pagination ?? null);
      })
      .catch((err: unknown) => {
        if (requestIdRef.current !== requestId) return;
        const status = axios.isAxiosError(err)
          ? err.response?.status ?? null
          : null;
        setData([]);
        setPagination(null);
        setError(extractErrorMessage(err, "Failed to load audit logs"));
        setErrorStatus(status);
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setIsFetching(false);
        setHasFetched(true);
      });
    // `paramsKey` captures all relevant fields of `params`; `params` itself
    // is intentionally NOT in the dep array (identity changes shouldn't
    // refetch).
  }, [api, paramsKey, refreshNonce]);

  const refetch = useCallback(() => {
    setRefreshNonce((n) => n + 1);
  }, []);

  return {
    data,
    pagination,
    isFetching,
    hasFetched,
    error,
    errorStatus,
    refetch,
  };
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as { message?: string } | undefined;
    if (body?.message) return body.message;
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
